import type { AcademySport, AgeGroup, Lesson } from '@court-sync/shared';
import type { LessonFiltersType } from '../components/public/LessonFilters';
import { lessonMatchesSportFilter } from './publicBrowseMode';

export type PublicBrowseFilterContext = {
  filters: LessonFiltersType;
  /** Validated program id from `?program=` (null when unset or invalid). */
  programId: string | null;
  /** Validated custom age group id from `?ageGroupId=` (null when unset or invalid). */
  ageGroupId: string | null;
  /** Validated academy_sports id from `?sport=` (null when unset or invalid). */
  academySportId: string | null;
  ageGroups: AgeGroup[];
  academySports: readonly AcademySport[];
};

export function resolvePublicProgramFilterId(
  searchParams: URLSearchParams,
  publicProgramIds: ReadonlySet<string> | string[]
): string | null {
  const programParam = searchParams.get('program');
  if (!programParam || programParam === 'all') return null;
  const ids = publicProgramIds instanceof Set ? publicProgramIds : new Set(publicProgramIds);
  return ids.has(programParam) ? programParam : null;
}

export function resolvePublicAgeGroupFilterId(
  searchParams: URLSearchParams,
  ageGroups: AgeGroup[]
): string | null {
  const ageGroupParam = searchParams.get('ageGroupId');
  if (!ageGroupParam || ageGroupParam === 'all') return null;
  return ageGroups.some((g) => g.id === ageGroupParam) ? ageGroupParam : null;
}

function matchesLegacyAgeGroupFilter(
  lesson: Lesson,
  filterAgeGroup: NonNullable<LessonFiltersType['ageGroup']>,
  ageGroups: AgeGroup[]
): boolean {
  const lessonAgeGroup = lesson.ageGroup ?? 'all';
  if (lessonAgeGroup === filterAgeGroup) return true;

  if (!lesson.ageGroupId) {
    return lessonAgeGroup === filterAgeGroup;
  }

  if (filterAgeGroup === 'all') return true;

  const customGroup = ageGroups.find((g) => g.id === lesson.ageGroupId);
  if (!customGroup) return false;

  if (customGroup.method === 'ball_color') {
    return filterAgeGroup === 'juniors';
  }

  if (customGroup.method === 'age_range') {
    const maxAge = customGroup.maxAge ?? customGroup.minAge;
    if (maxAge != null && maxAge < 18) return filterAgeGroup === 'juniors';
    if (customGroup.minAge != null && customGroup.minAge >= 18) return filterAgeGroup === 'adults';
  }

  if (customGroup.method === 'custom') {
    return lessonAgeGroup !== 'all' && lessonAgeGroup === filterAgeGroup;
  }

  return false;
}

/**
 * Shared public browse filters for term/group lessons, ungrouped camps, and grouped camp instances.
 */
export function matchesPublicBrowseFilters(
  lesson: Lesson,
  ctx: PublicBrowseFilterContext,
  searchFallbackText?: string
): boolean {
  if (ctx.programId && lesson.programId !== ctx.programId) {
    return false;
  }

  if (ctx.ageGroupId && lesson.ageGroupId !== ctx.ageGroupId) {
    return false;
  }

  if (!lessonMatchesSportFilter(lesson, ctx.academySportId, ctx.academySports)) {
    return false;
  }

  if (ctx.filters.searchQuery) {
    const query = ctx.filters.searchQuery.toLowerCase();
    const matchesSearch =
      lesson.name.toLowerCase().includes(query) ||
      lesson.description?.toLowerCase().includes(query) ||
      (searchFallbackText?.toLowerCase().includes(query) ?? false);
    if (!matchesSearch) return false;
  }

  if (ctx.filters.lessonType && lesson.lessonType !== ctx.filters.lessonType) {
    return false;
  }

  if (ctx.filters.skillLevel && lesson.skillLevel !== ctx.filters.skillLevel) {
    return false;
  }

  if (ctx.filters.ageGroup && !matchesLegacyAgeGroupFilter(lesson, ctx.filters.ageGroup, ctx.ageGroups)) {
    return false;
  }

  if (ctx.filters.dayOfWeek !== undefined) {
    if (!lesson.daysOfWeek?.includes(ctx.filters.dayOfWeek)) return false;
  }

  if (ctx.filters.termId && lesson.termId !== ctx.filters.termId) {
    return false;
  }

  if (ctx.filters.maxPrice != null && lesson.price > ctx.filters.maxPrice) {
    return false;
  }

  if (ctx.filters.locationId && lesson.locationId !== ctx.filters.locationId) {
    return false;
  }

  return true;
}
